/**
 * useImportJob — thin async wrapper around /api/import/* that drives the
 * SmartImport reducer. Generic because Brian may want to reuse the job-shape
 * polling outside the Smart Import page (e.g. ETL re-run UI) later.
 */

import { useCallback, useRef, useState } from 'react'
import { ImportApiError, importApi } from '../pages/SmartImport/api'
import type {
  ColumnConfirmResult,
  ConfirmResult,
  JobRecord,
  MmOption,
  MmSession,
  UploadResult,
} from '../pages/SmartImport/api'

interface UseImportJob {
  busy: boolean
  lastError: string | null
  clearError: () => void
  getSession: () => Promise<MmSession>
  connect: (body: { auth: string; session: string; alb?: string }) => Promise<boolean>
  disconnect: () => Promise<void>
  upload: (file: File) => Promise<UploadResult>
  confirmColumn: (jobId: string, columnIndex: number) => Promise<ColumnConfirmResult>
  loadEvents: (jobId: string) => Promise<MmOption[]>
  loadPlans: (jobId: string, eventId: number) => Promise<MmOption[]>
  submit: (
    jobId: string,
    body: {
      name: string
      eventId: number
      planId: number
      isAutoRedeemed?: boolean
      allowOverwriteForDuplicate?: boolean
    },
  ) => Promise<ConfirmResult>
  pollJob: (jobId: string, intervalMs?: number) => Promise<JobRecord>
  cancelPolling: () => void
}

export function useImportJob(): UseImportJob {
  const [busy, setBusy] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const cancelRef = useRef<(() => void) | null>(null)

  const wrap = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      setBusy(true)
      setLastError(null)
      try {
        return await fn()
      } catch (err) {
        const msg =
          err instanceof ImportApiError
            ? err.message
            : err instanceof Error
            ? err.message
            : 'Unexpected error'
        setLastError(msg)
        throw err
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  const pollJob = useCallback(
    (jobId: string, intervalMs = 1000): Promise<JobRecord> => {
      return new Promise((resolve, reject) => {
        let stopped = false
        const stop = () => {
          stopped = true
        }
        cancelRef.current = stop

        const tick = async () => {
          if (stopped) return
          try {
            const job = await importApi.getJob(jobId)
            if (stopped) return
            if (job.status === 'submitted' || job.status === 'failed') {
              resolve(job)
              return
            }
            setTimeout(tick, intervalMs)
          } catch (err) {
            reject(err)
          }
        }
        tick()
      })
    },
    [],
  )

  return {
    busy,
    lastError,
    clearError: () => setLastError(null),
    getSession: () => importApi.getMmSession(),
    connect: (body) =>
      wrap(async () => {
        const r = await importApi.mmConnect(body)
        if (!r.ok) throw new Error(r.error ?? 'Connection failed')
        return true
      }),
    disconnect: () =>
      wrap(async () => {
        await importApi.mmDisconnect()
      }),
    upload: (file) => wrap(() => importApi.uploadFile(file)),
    confirmColumn: (jobId, columnIndex) =>
      wrap(() => importApi.confirmColumn(jobId, columnIndex)),
    loadEvents: (jobId) => wrap(() => importApi.getEvents(jobId)),
    loadPlans: (jobId, eventId) => wrap(() => importApi.getPlans(jobId, eventId)),
    submit: (jobId, body) => wrap(() => importApi.confirmJob(jobId, body)),
    pollJob,
    cancelPolling: () => {
      cancelRef.current?.()
      cancelRef.current = null
    },
  }
}
